import { Loading } from '@rd-web-markets/shared/dist/util';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Breadcrumb } from 'react-bootstrap';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { useDispatch } from 'react-redux';
import claimGroupService from '@rd-web-markets/shared/dist/services/claim_group.service';
import ClaimProjectReportDetails from '@rd-web-markets/market/dist/technicalProof/ClaimProjectReportDetails';
import BreadcrumbsPortal from '@rd-web-markets/shared/dist/util/BreadcrumbsPortal';
import SidebarPortal from '@rd-web-markets/shared/dist/util/SidebarPortal';
import { useTranslation } from 'react-i18next';
import { MARKET_SPECIFIC_RD_OR_4_MENU, SHOULD_AUTOSAVE } from '@rd-web-markets/market/dist/constants';
import { makeKey } from '@rd-web-markets/shared/dist/hooks/useConvertForTranslationKey';
import { CompanyService } from '@services/company.service';
import claimProjectReportsListsService from '@rd-web-markets/shared/dist/services/lists/claim_groups/claim_project_reports_lists.service';
import claimProjectReportService from '@rd-web-markets/shared/dist/services/project_report/claim_project_report.service';
import { createTimezonedDate } from '@rd-web-markets/shared/dist/util/date';
import GeneralBreadcrumb from '@rd-web-markets/shared/dist/util/breadcrumbs/GeneralBreadcrumb';
import { USE_V2_DESIGN } from '@rd-web-markets/market/dist/constants';
import ClaimGroupSidebarV2 from '@rd-web-markets/shared/dist/pages/setup/ClaimGroupSidebarV2';

const extractMonthYear = (dateString) => {
  if (dateString) {
    const month = dateString.getMonth() + 1;
    const year = dateString.getFullYear();
    return [month, year].join('/');
  }
};
const extractDayMonthYear = (dateString) => {
  if (dateString) {
    const day = dateString.getDay() + 1;
    const month = dateString.getMonth() + 1;
    const year = dateString.getFullYear();
    return [day, month, year].join('/');
  }
};

const ClaimProjectReportDetailPage = ({accountType, handleToaster}) => {
  const { claimGroupId, report_id } = useParams();
  const [report, setReport] = claimProjectReportService.useGet(claimGroupId, report_id)
  const [claimGroup, setClaimGroup, resetClaimGroup] = claimGroupService.useGetClaimGroup(claimGroupId);
  const [company, setCompany] = useState(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const [isSaved, setIsSaved] = useState(true);
  const { t } = useTranslation();
  const [allReports, setAllReports] = useState([])
  const [originalProjectData, setOriginalProjectData] = useState(null)

  useEffect(() => {
    const originalProjectDataIsForDifferentProject = report?.id !== originalProjectData?.id
    if (report?.id && (!originalProjectData || originalProjectDataIsForDifferentProject)) {
      setOriginalProjectData({...report})
    }
  }, [originalProjectData, report])

  useEffect(() => {
    const fetchData = async () => {
      if (claimGroup) {
        const company = await CompanyService.get(claimGroup.company_id)
        setCompany(company);
      }
    }
    fetchData();

  }, [dispatch, claimGroup, claimGroupId]);

  function shouldConfirmReportDateChange(report) {
    if (process.env.PUBLIC_URL !== '/be' || !report?.id || !originalProjectData?.id) {
      return false
    }

    let datesHaveBeenEditted = false
    if (report.start_date !== originalProjectData.start_date || report.end_date !== originalProjectData.end_date) {
      datesHaveBeenEditted = true
    }      

    return datesHaveBeenEditted
  }

  const areClaimGroupYearWithinProjectYears = (projectStartDate, projectEndDate, claimGroup) => {
    const projectStartYear = createTimezonedDate(projectStartDate).getFullYear();
    const projectEndYear = createTimezonedDate(projectEndDate).getFullYear();
    const claimGroupStartYear = createTimezonedDate(claimGroup.from_date).getFullYear();
    return projectStartYear <= claimGroupStartYear && claimGroupStartYear <= projectEndYear;
  };

  function canProceedChangingReport(report) {
    if (!shouldConfirmReportDateChange(report)) {
      return true
    } else {
      if (!areClaimGroupYearWithinProjectYears(report.start_date, report.end_date, claimGroup)) {
        handleToaster(t('you_cannot_remove_the_project_from_the_mission_add_it_to_another_mission_first'), 'warning');
        return false
      }

      if (!window.confirm(t('are_you_sure_you_want_to_change_the_project_dates'))) {
        return false
      }

      return true
    }
  }

  const handleChange = (event) => {
    console.log('here')
    const { name, type, checked, value } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    if (name === 'assessment') {
      setReport({
        ...report,
        assessment: newValue,
        completion_status: null, // clear it
      });
    } else {
      setReport({
        ...report,
        [name]: newValue,
      });
    }

    setIsSaved(false);
  };

  const handleSliceChange = (event, index) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;

    const slices = report.project_report_slices;
    slices[index][event.target.name] = value;
    setReport({...report, project_report_slices: [...slices]})
    //handleSubmit();
  };
  const handleDelete = async e => {
    e.preventDefault();
    try { 
      if(window.confirm('Are you sure you want to delete this project and all of its files?')){
        await claimProjectReportService.delete(claimGroupId, report.id);
        history.replace(`/${accountType}/claim_groups/${claimGroupId}/project_overview`);
      }
    } catch(error) {
      dispatch(handleError(error))
    }
  }

  const handleTinyMce = (content, name, index) => {
    const slices = report.project_report_slices;
    index = slices.findIndex(slice => slice.title === name)
    if (index < 0) {
      return
    }

    slices[index]['body'] = content;
    setReport({...report, project_report_slices: [...slices]})
  };

  const toggleOngoing = event => {
    if (event.target.checked === true) {
      setReport({
        ...report,
        end_date: null
      });
    }
    else {
      if(process.env.PUBLIC_URL === '/it'){
        setReport({
          ...report,
          end_date: extractDayMonthYear(new Date())
        });
      }else{
        setReport({
          ...report,
          end_date: extractMonthYear(new Date())
        });
      }
    }
  }

  const updateReport = useCallback(async (updatedFields = {}) => {
    try {
      if (!canProceedChangingReport(report)) {
        return
      }

      // Fixes a bug where project notes and project technical narrative fields are not being saved when editted
      report.project_report_slices_attributes = report.project_report_slices

      const updatedReport = await claimProjectReportService.update(claimGroupId, {...report, ...updatedFields});
      setIsSaved(true)
      setReport(report => ({
        ...report,
        ...updatedReport
      }))
    } catch(error) {
      dispatch(handleError(error))
    }
  }, [canProceedChangingReport, report, claimGroupId, setReport, dispatch]);

  const handleSubmit = useCallback(async (e, shouldOverrideField = false) => {
    e?.preventDefault();
    if (shouldOverrideField === true && e?.target?.name) {
      const { name, type, checked, value } = e.target;
      const newValue = type === 'checkbox' ? checked : value;

      await updateReport({ [name]: newValue });
    } else {
      await updateReport();
    }
    resetClaimGroup(claimGroupId);
  }, [updateReport, claimGroupId, resetClaimGroup]);

  const handleCheckChange = async (e) => {
    try {
      if (!canProceedChangingReport(report)) {
        return
      }

      const response = await claimProjectReportService.update(claimGroupId, {...report, [e.target.name]: e.target.checked});
      setReport({
        ...response
      });
      resetClaimGroup(claimGroupId);
    } catch (error) {
      dispatch(handleError(error))
    }
  }

  const handleDateChange = async (e) => {
    try {
      if (!canProceedChangingReport(report)) {
        return
      }

      const updatedFields = { [e.target.name]: e.target.value };
      await claimProjectReportService.update(claimGroupId, {...report, ...updatedFields});
      setReport({
        ...report,
        ...updatedFields
      });
    } catch (error) {
      dispatch(handleError(error))
    }
  }

    const fetchReports = useCallback(async () => {
      const response = await claimProjectReportsListsService.base.all(claimGroupId, {group_projects: false, order_by: 'report_order'})
      setAllReports([...response.claim_project_reports])
    }, [claimGroupId])

  useEffect(() => {
    if (claimGroup?.projects_count <= 20){
      fetchReports()
    }
  }, [report_id, claimGroup?.projects_count, fetchReports])
  
  
  useEffect(() => {
    if (SHOULD_AUTOSAVE && report) {
      updateReport();
    }
  }, [report, updateReport]);

  if(!report || !claimGroup || !company) return <Loading />

  const isOngoing = report.end_date === null ? true : false;

  const menuItems = allReports.map((report) => {
    return {
      link: `claim_groups/${claimGroup.id}/technical_proof/project_reports/${report.id}?key=project_report_tab`,
      text: report.project_name,
      highlighted: report.id === +report_id,
    };
  });

  const projectNameAndLink = {
    text: report.project_name, 
    link: '',
  }

  return (
    <>
      {USE_V2_DESIGN ? 
        <ClaimGroupSidebarV2
          claimGroup={claimGroup}
          company={company}
          claims={claimGroup.claims}
          accountType={accountType}
          highlightedMenuItem={"projects"}
        /> : <SidebarPortal headerItem={{link: `claim_groups/${claimGroupId}/project_overview`, text: t('project_overview')}} menuItems={menuItems} />
      }


      {process.env.PUBLIC_URL === '/de' ? (
        <GeneralBreadcrumb claimGroup={claimGroup} items={['home', 'companies', 'company', 'bsfz', projectNameAndLink]}/>
      ): (
        <BreadcrumbsPortal>
          <Breadcrumb>
            <Breadcrumb.Item linkAs={Link} linkProps={{to: `/${accountType}`}}>{t('home')}</Breadcrumb.Item>
            <Breadcrumb.Item linkAs={Link} linkProps={{to: `/${accountType}/companies`}}>{t('companies')}</Breadcrumb.Item>
            <Breadcrumb.Item linkAs={Link} linkProps={{to: `/${accountType}/companies/${claimGroup.company_id}/master`}}>{claimGroup.company.name}</Breadcrumb.Item>
            <Breadcrumb.Item linkAs={Link} linkProps={{to: `/${accountType}/claim_groups/${claimGroupId}/project_overview`}}>{claimGroup.name.replace(claimGroup.company.name, '').trim()}</Breadcrumb.Item>
            <Breadcrumb.Item active>{report.project_name}</Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbsPortal>
      )}

      {report && (
        <ClaimProjectReportDetails
          claimGroup={claimGroup}
          handleCheckChange={handleCheckChange}
          handleDateChange={handleDateChange}
          report={report}
          company={company}
          handleSubmit={handleSubmit}
          handleChange={handleChange}
          toggleOngoing={toggleOngoing}
          setReport={setReport}
          handleSliceChange={handleSliceChange}
          isOngoing={isOngoing}
          handleDelete={handleDelete}
          handleTinyMce={handleTinyMce}
          accountType={accountType}
          setClaimGroup={setClaimGroup}
          handleToaster={handleToaster}
          isSaved={isSaved}
          fetchReports={fetchReports}
          originalProjectData={originalProjectData} //be
        />
      )}
    </>
  );
}

export default ClaimProjectReportDetailPage
